import React, { ReactNode, useMemo } from 'react';

import { useWindowSize } from 'react-use';
import { Button } from '@mui/material';

import classes from './ChallengeLandingContent.module.scss';
import { BlackBgRow } from '../../../shared/components/ui-components';
import { ChallengeTitleBlock } from '../../components/ChallengeTitleBlock/ChallengeTitleBlock';
import { DetailsBlock } from './DetailsBlock/DetailsBlock';
import { PrizesBlock } from './PrizesBlock/PrizesBlock';
import { HowItsWorkBlock } from './HowItsWorkBlock/HowItsWorkBlock';
import { FaqBlock } from './FaqBlock/FaqBlock';
import { getRouteParam, goTo } from '../../../shared/utils/angularUtils';
import { ChallengeInfo } from '../../interfaces/ChallengeInfo';
import { CHALLENGE_DEFAULT_BANNER } from '../../constants/challengeDefaultBanner';
import { getIsRegistrationPeriod } from '../../utils/getIsRegistrationPeriod/getIsRegistrationPeriod';
import { JoinChallengeButton } from '../../components/JoinChallengeButton/JoinChallengeButton';

type ChallengeLandingContentProps = {
  headerBlock?: ReactNode;
  challengeInfo: ChallengeInfo;
};
const ChallengeLandingContent = ({
  challengeInfo,
  headerBlock,
}: ChallengeLandingContentProps) => {
  const challengeId = getRouteParam('challengeId');
  const { challenge, host, webBanner, mobileBanner } = challengeInfo;

  const { width } = useWindowSize();
  const isMobile = width < 768;

  if (!challengeId) {
    throw new Error('Challenge ID is not defined');
  }

  const handleOpenLeaderboardClick = () => {
    goTo(`/challenge-leaderboard/${challengeId}`);
  };

  const bannerUrl = useMemo(
    () =>
      isMobile
        ? mobileBanner?.fileUrl || CHALLENGE_DEFAULT_BANNER.MOBILE
        : webBanner?.fileUrl || CHALLENGE_DEFAULT_BANNER.WEB,
    [isMobile],
  );

  const isRegistrationPeriod = getIsRegistrationPeriod(
    challenge.importantDates.registrationStartDate,
    challenge.importantDates.registrationEndDate,
  );

  return (
    <>
      <BlackBgRow bgRowHeight={isMobile ? '622px' : '588px'}>
        <div className={classes.container}>
          {headerBlock}
          <ChallengeTitleBlock
            challengeId={challengeId}
            title={challenge.about.challengeName}
            description={challenge.about.description}
            isRegistrationPeriod={isRegistrationPeriod}
            challengeStatus={challenge.status}
            openButtonTitle="Open Leaderboard"
            onOpenButtonClick={handleOpenLeaderboardClick}
          />
          {bannerUrl && (
            <img
              className={classes.challengeImage}
              src={bannerUrl}
              alt="Challenge banner"
            />
          )}
          <div className={classes.infoSection}>
            <DetailsBlock
              price={challenge.about.price}
              importantDates={challenge.importantDates}
              host={host}
            />
            <div className={classes.rightColumn}>
              <PrizesBlock prizes={challenge.prizes} defaultOpen={!isMobile} />
              <HowItsWorkBlock
                defaultOpen={!isMobile}
                aboutInfo={challenge.about.about}
                additionalInfo={challenge.about.additionalInfo}
                scoringRules={challenge.about.scoringRules}
              />
              <FaqBlock faqs={challenge.faqs} defaultOpen={!isMobile} />
            </div>
          </div>
          <div className={classes.buttons}>
            <JoinChallengeButton
              challengeId={challengeId}
              challengeStatus={challenge.status}
              isRegistrationPeriod={isRegistrationPeriod}
            />
            <Button variant="outlined" onClick={handleOpenLeaderboardClick}>
              Open Leaderboard
            </Button>
          </div>
        </div>
      </BlackBgRow>
    </>
  );
};

export { ChallengeLandingContent };
